import { useEffect } from 'react';

/**
 * Embedded booking pages (scheduler-widget.js popup / inline embed).
 *
 * The widget loads the booking page in an iframe inside its OWN white box, so
 * a page that also draws its full-page layout (grey background, padded card
 * with border + shadow) shows up as a box inside a box. scheduler-widget.js
 * adds `embed=1` to the iframe URL; webinar pages read it, drop their outer
 * chrome, and report their content height so the widget can size its box to
 * the form — one box, one scrollbar.
 *
 * Kept in sync with web/public/scheduler-widget.js (plain JS, no imports).
 */
export const EMBED_QUERY_PARAM = 'embed';
export const EMBED_RESIZE_MESSAGE = 'px-scheduler:resize';

export function isEmbedRequested(search: string): boolean {
    try {
        const value = new URLSearchParams(search).get(EMBED_QUERY_PARAM);
        return value === '1' || value === 'true';
    } catch {
        return false;
    }
}

/** Carries the embed flag across in-app navigation (booking → success). */
export function withEmbedParam(path: string, embedded: boolean): string {
    if (!embedded) return path;
    return `${path}${path.includes('?') ? '&' : '?'}${EMBED_QUERY_PARAM}=1`;
}

/**
 * Posts the element's rendered height to the embedding page whenever it
 * changes. Only the height and a layout hint are sent — nothing about the
 * booking — so a wildcard target origin is safe here.
 *
 * Takes the element itself (from a callback ref held in state), not a ref
 * object: the measured node only mounts after the page finishes loading, and
 * a ref object would not re-run this effect when it appears.
 */
export function useEmbedAutoHeight(
    el: HTMLElement | null,
    enabled: boolean,
    layout: 'webinar' = 'webinar',
): void {
    useEffect(() => {
        if (!enabled || !el || typeof window === 'undefined' || window.parent === window) return;

        let last = 0;
        const post = () => {
            const height = Math.ceil(el.getBoundingClientRect().height);
            if (!height || height === last) return;
            last = height;
            try {
                window.parent.postMessage({ type: EMBED_RESIZE_MESSAGE, height, layout }, '*');
            } catch {
                /* parent unreachable — the widget keeps its default size */
            }
        };

        post();
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(post);
        observer.observe(el);
        return () => observer.disconnect();
    }, [el, enabled, layout]);
}
